import { Component } from '@theme/component';
import { debounce, onDocumentReady } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';
import { getFocusableElements } from '@theme/focus';

const SHORT_ACTIVATE_DELAY = 0;
const LONG_ACTIVATE_DELAY = 80;
const DEACTIVATE_DELAY = 450;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    this.overflowMenu?.addEventListener(
      'keydown',
      (event) => {
        const moreButton = this.querySelector('[slot="more"] [ref="menuitem"]');
        this.#handlePanelKeydown(event, moreButton);
      },
      { signal: this.#abortController.signal }
    );

    // Fallback: whatever the reason focus actually left the overflow panel (native Tab past
    // the last item when there's nothing to redirect to, a click elsewhere, etc.), make sure
    // it closes rather than staying stuck open.
    this.overflowMenu?.addEventListener(
      'focusout',
      (event) => {
        if (event.relatedTarget instanceof Node && this.overflowMenu?.contains(event.relatedTarget)) return;
        this.#deactivate(this.#state.activeItem);
      },
      { signal: this.#abortController.signal }
    );

    // Manage hover persistence across external mega-repeater panels
    const repeaterPanels = document.querySelectorAll(
      '.header__mega-repeater-container [data-match-handle]'
    );
    repeaterPanels.forEach((panel) => {
      const handle = panel.getAttribute('data-match-handle');
      const trigger = handle ? this.querySelector(`.menu-list__link[data-menu-handle="${handle}"]`) : null;

      panel.addEventListener(
        'pointerenter',
        () => {
          this.#debouncedDeactivate.cancel();
        },
        { signal: this.#abortController.signal }
      );
      panel.addEventListener(
        'pointerleave',
        () => {
          this.#debouncedDeactivate();
        },
        { signal: this.#abortController.signal }
      );
      panel.addEventListener(
        'keydown',
        (event) => this.#handlePanelKeydown(event, trigger),
        { signal: this.#abortController.signal }
      );
      // Fallback: same as the overflow panel above — guarantees the dropdown closes whenever
      // focus actually leaves it, even in edge cases the Tab-keydown redirect doesn't catch
      // (e.g. the last dropdown in the nav, where there's no next trigger to redirect to).
      panel.addEventListener(
        'focusout',
        (event) => {
          if (event.relatedTarget instanceof Node && panel.contains(event.relatedTarget)) return;
          this.#deactivate(trigger);
        },
        { signal: this.#abortController.signal }
      );
    });

    onDocumentReady(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#shortDebouncedActivateHandler.cancel();
    this.#longDebouncedActivateHandler.cancel();

    if (this.#state.activeItem) {
      this.#shortDebouncedActivateHandler(event);
    } else {
      this.#longDebouncedActivateHandler(event);
    }
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    // Indicate that we're switching between open items to suppress slide-down animation
    this.toggleAttribute('data-switching', Boolean(previouslyActiveItem));

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      previouslyActiveItem.classList.remove('is-hovered');
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';
    item.classList.add('is-hovered');

    const moreButton = this.querySelector('[slot="more"] [ref="menuitem"]');
    if (moreButton) moreButton.ariaExpanded = (!isDefaultSlot).toString();

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
  };

  #shortDebouncedActivateHandler = debounce(this.#activateHandler, SHORT_ACTIVATE_DELAY);
  #longDebouncedActivateHandler = debounce(this.#activateHandler, LONG_ACTIVATE_DELAY);

  /**
   * Explicitly open/close a dropdown from the keyboard (Enter/Space), moving focus into the
   * opened panel, or close it and return focus to the trigger on Escape. Bound via `on:keydown`.
   * @param {KeyboardEvent} event
   */
  handleTriggerKeydown = (event) => {
    if (!(event.target instanceof Element)) return;

    const li = event.target;
    const isMore = li.matches('[slot="more"]');
    const item = findMenuItem(li);

    if (event.key === 'Escape') {
      if (!item || item !== this.#state.activeItem) return;
      event.preventDefault();
      const trigger = isMore ? this.querySelector('[slot="more"] [ref="menuitem"]') : item;
      trigger?.focus();
      this.#deactivate(item);
      return;
    }

    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (!item) return;
    if (!isMore && item.getAttribute('aria-haspopup') !== 'true') return;

    event.preventDefault();

    if (item !== this.#state.activeItem) {
      this.#debouncedDeactivate.cancel();
      this.#shortDebouncedActivateHandler.cancel();
      this.#longDebouncedActivateHandler.cancel();
      this.#activateHandler(event);
    }

    if (isMore) {
      item.focus();
      return;
    }

    const handle = item.dataset.menuHandle;
    const panel = handle
      ? document.querySelector(`.header__mega-repeater-container [data-match-handle="${handle}"]`)
      : null;
    const [firstFocusable] = panel ? getFocusableElements(panel) : [];
    firstFocusable?.focus();
  };

  /**
   * Handle Escape and Tab-boundary keydowns while focus is inside an open panel. The panel is a
   * DOM sibling of the header row rather than of its trigger, so natural Tab order doesn't lead
   * anywhere sensible — redirect it manually instead of moving markup.
   * @param {KeyboardEvent} event
   * @param {Element | null | undefined} trigger
   */
  #handlePanelKeydown = (event, trigger) => {
    const item = this.#state.activeItem;

    if (event.key === 'Escape') {
      event.preventDefault();
      trigger?.focus();
      this.#deactivate(item);
      return;
    }

    if (event.key !== 'Tab') return;
    if (!(event.currentTarget instanceof HTMLElement)) return;

    const focusable = getFocusableElements(event.currentTarget);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      const next = findNextTrigger(trigger) ?? trigger;
      next?.focus();
      this.#deactivate(item);
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      trigger?.focus();
      this.#deactivate(item);
    }
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#shortDebouncedActivateHandler.cancel();
    this.#longDebouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;
    if (this.matches(':hover')) return;

    // Keep active while matching mega-repeater panel is hovered or focused
    const handle = item?.dataset?.menuHandle;
    if (handle) {
      const matchingPanel = document.querySelector(`.header__mega-repeater-container [data-match-handle="${handle}"]`);
      if (matchingPanel?.matches(':hover')) return;
      if (matchingPanel && document.activeElement instanceof Node && matchingPanel.contains(document.activeElement)) {
        return;
      }
    }

    // Keep active while focus is inside the overflow panel
    if (this.overflowMenu && document.activeElement instanceof Node && this.overflowMenu.contains(document.activeElement)) {
      return;
    }

    this.style.setProperty('--submenu-height', '0px');
    this.dataset.overflowExpanded = 'false';
    this.removeAttribute('data-switching');

    const moreButton = this.querySelector('[slot="more"] [ref="menuitem"]');
    if (moreButton) moreButton.ariaExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.classList.remove('is-hovered');
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, this.animationDelay);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"]')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the next top-level trigger's menu item, in light-DOM order (unaffected by overflow slotting).
 * @param {Element | null | undefined} trigger
 * @returns {HTMLElement | null}
 */
function findNextTrigger(trigger) {
  const nextLi = trigger?.closest('li')?.nextElementSibling;
  return nextLi?.querySelector('[ref="menuitem"]') ?? null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
